<template>
  <ArchiveModal
    :open="open"
    title="Archives guide"
    subtitle="How to file, find and share the Administrative Division's records."
    size="guide"
    flush
    @close="$emit('close')"
  >
    <div class="ar-guide">
      <nav ref="nav" class="ar-guide-nav" aria-label="Guide topics">
        <button
          v-for="t in topics"
          :key="t.key"
          type="button"
          :class="{ on: topic === t.key }"
          :aria-current="topic === t.key ? 'true' : null"
          @click="go(t.key)"
        >
          <i class="bi" :class="t.icon" aria-hidden="true"></i>
          <span>{{ t.label }}</span>
        </button>
      </nav>

      <article ref="body" class="ar-guide-body" tabindex="-1">
        <!-- Getting started -->
        <section v-if="topic === 'start'">
          <h3>Getting started</h3>
          <p class="ar-guide-lead">
            The Archives keep the Administrative Division's files in one place, like a shared drive. Each office has a
            <strong>cabinet</strong>, and inside a cabinet files are kept in <strong>folders</strong>.
          </p>
          <ol class="ar-guide-steps">
            <li>
              <strong>Open a cabinet</strong>
              <span>You see only the cabinets you've been added to. Your access in each one is shown on its card.</span>
            </li>
            <li>
              <strong>Upload</strong>
              <span>Open a folder, then click <em>Upload</em> or drag files onto the page. Each file can be up to {{ maxSize }}.</span>
            </li>
            <li>
              <strong>Find it later</strong>
              <span>Search on the Archives home looks through file names, folder names and notes in all your cabinets.</span>
            </li>
          </ol>
          <h4>What you can do in a cabinet</h4>
          <dl class="ar-guide-levels">
            <div v-for="level in levels" :key="level">
              <dt><span class="ar-badge" :class="level"><i class="bi" :class="ACCESS[level].icon"></i> {{ ACCESS[level].label }}</span></dt>
              <dd>{{ ACCESS[level].hint }}</dd>
            </div>
          </dl>
          <p v-if="manageAll" class="ar-guide-callout">
            <i class="bi bi-shield-check" aria-hidden="true"></i>
            <span>
              You're an <strong>Archives administrator</strong>: you can open and manage every cabinet, add cabinets, group
              them into categories (<em>Organize</em> on the Archives home), and change the upload settings.
            </span>
          </p>
        </section>

        <!-- What goes where -->
        <section v-else-if="topic === 'where'">
          <h3>What goes where</h3>
          <p class="ar-guide-lead">
            File each record in the cabinet of the office that owns it. Typical contents of each office's cabinet:
          </p>
          <p>
            On the Archives home, cabinets can be grouped into categories (for example Finance for Accounting and Budget).
            Click a category's name to fold it away.
          </p>
          <ul class="ar-guide-where">
            <li v-for="office in offices" :key="office.name">
              <span class="ar-cab-dot" :style="{ background: office.color }"><i class="bi" :class="office.icon"></i></span>
              <div>
                <strong>{{ office.name }}</strong>
                <span>{{ office.examples }}</span>
              </div>
            </li>
          </ul>
          <p class="ar-guide-tip">
            <i class="bi bi-lightbulb" aria-hidden="true"></i>
            <span>
              Not sure where something belongs? Put it where people would look first and add a note saying what it is, or ask
              the cabinet's manager.
            </span>
          </p>
        </section>

        <!-- Uploading -->
        <section v-else-if="topic === 'upload'">
          <h3>Uploading</h3>
          <p class="ar-guide-lead">
            Open the folder you want, then click <strong>Upload</strong> or drag files from your computer onto the page.
            You can choose several files at once. You need <em>Contributor</em> or <em>Manager</em> access to upload.
          </p>
          <div class="ar-guide-limits">
            <div class="ar-guide-limit">
              <i class="bi bi-file-earmark-arrow-up" aria-hidden="true"></i>
              <div>
                <strong>Up to {{ maxSize }} per file</strong>
                <span>Big files are sent in small pieces, so a short connection drop won't make you start over.</span>
              </div>
            </div>
            <div class="ar-guide-limit">
              <i class="bi bi-hdd" aria-hidden="true"></i>
              <div>
                <strong>Some cabinets have a storage limit</strong>
                <span>It shows under the cabinet, like "1.2 GB of 5 GB". Files in the Trash count until it's emptied.</span>
              </div>
            </div>
            <div class="ar-guide-limit">
              <i class="bi bi-calendar-range" aria-hidden="true"></i>
              <div>
                <strong>Uploads can be closed</strong>
                <span>
                  A cabinet's managers can close uploads, or open them only between set dates and times (for example a
                  submission period). While they're closed nobody can upload, and the cabinet shows when they open again.
                  A file still uploading when they close isn't saved.
                </span>
              </div>
            </div>
          </div>
          <h4>Files you can upload</h4>
          <ul class="ar-guide-types">
            <li v-for="group in groups" :key="group.key">
              <strong>{{ group.label }}</strong>
              <span class="ar-ext-list">
                <span v-for="ext in group.extensions" :key="ext" class="ar-ext">.{{ ext }}</span>
              </span>
            </li>
          </ul>
          <p class="ar-guide-note">Programs and scripts (such as .exe or .bat files) can never be uploaded.</p>
          <h4>Scanning tips</h4>
          <ul class="ar-guide-list">
            <li>Scan paper records to <strong>PDF</strong> at 200–300 dpi. Black and white keeps plain text small.</li>
            <li>Keep <strong>one document in one PDF</strong>, with all of its pages and attachments together.</li>
            <li>Keep the page open until the upload list in the corner says the files are done.</li>
          </ul>
        </section>

        <!-- Folders and names -->
        <section v-else-if="topic === 'names'">
          <h3>Folders and file names</h3>
          <p class="ar-guide-lead">
            A simple pattern that everyone follows makes records easy to find years later:
            <strong>year</strong>, then <strong>kind of record</strong>, then <strong>month</strong> if there are many.
          </p>
          <div class="ar-guide-tree" aria-label="Example folders">
            <div><i class="bi bi-folder-fill tone-folder"></i> 2026</div>
            <div class="d1"><i class="bi bi-folder-fill tone-folder"></i> Disbursement Vouchers</div>
            <div class="d1"><i class="bi bi-folder-fill tone-folder"></i> Payroll</div>
            <div class="d2"><i class="bi bi-folder-fill tone-folder"></i> 10 October</div>
            <div class="d3"><i class="bi bi-file-earmark-pdf tone-pdf"></i> 2026-10-15 Payroll COS.pdf</div>
          </div>
          <h4>Naming files</h4>
          <ul class="ar-guide-list">
            <li>Start with the <strong>date as year-month-day</strong> (2026-10-15) so files sort in order.</li>
            <li>Then say what it is, and its reference number if it has one.</li>
          </ul>
          <div class="ar-guide-compare">
            <div class="good">
              <strong><i class="bi bi-check-circle-fill"></i> Clear</strong>
              <code>2026-10-15 DV 2026-10-0123 ABC Trading.pdf</code>
              <code>2026-10 Payroll COS.pdf</code>
            </div>
            <div class="bad">
              <strong><i class="bi bi-x-circle-fill"></i> Hard to find</strong>
              <code>scan0001.pdf</code>
              <code>final final (2).pdf</code>
            </div>
          </div>
          <p class="ar-guide-tip">
            <i class="bi bi-diagram-3" aria-hidden="true"></i>
            <span>
              Starting a new year? Open the menu (⋯) on last year's folder and choose <strong>Copy folder structure</strong>.
              It makes the same folders, empty (no files), and can change 2026 to 2027 in their names. The copy can go in
              this cabinet or another one you can add folders to.
            </span>
          </p>
          <p class="ar-guide-tip">
            <i class="bi bi-card-text" aria-hidden="true"></i>
            <span>
              Add <strong>notes</strong> to a file (its menu, then Details) with the payee, amount, subject or anything else
              people might search for. Search looks through notes too.
            </span>
          </p>
        </section>

        <!-- Finding files -->
        <section v-else-if="topic === 'find'">
          <h3>Finding files</h3>
          <ul class="ar-guide-list roomy">
            <li>
              <strong>Search</strong> on the Archives home looks through file names, folder names and notes in every
              cabinet you can open.
            </li>
            <li>Inside a folder, <strong>Filter</strong> narrows what's shown, and the sort menu orders by name, date, size or type.</li>
            <li>
              On a wide screen, <strong>click</strong> a file to see its details on the right. <strong>Double-click</strong> it,
              or click its name, to open it. Hold <kbd>Ctrl</kbd> (<kbd>⌘</kbd> on a Mac) and click to select several.
            </li>
            <li>PDFs, pictures and text files open in the preview. Other files download.</li>
            <li>The folder list on the left shows the whole cabinet. Click a folder to go straight to it.</li>
          </ul>
        </section>

        <!-- Google Docs and links -->
        <section v-else-if="topic === 'google'">
          <h3>Google Docs and links</h3>
          <p class="ar-guide-lead">
            Archived files are kept in the Administrative Division's Google Drive, in a private folder. They aren't shared
            by link: people reach them through CCMIS, which checks their access first.
          </p>
          <div class="ar-guide-limits">
            <div class="ar-guide-limit">
              <i class="bi bi-google" aria-hidden="true"></i>
              <div>
                <strong>Edit in Google Docs, Sheets or Slides</strong>
                <span>
                  For Word, Excel and PowerPoint files. CCMIS shares the file with your own Google account (your CCMIS
                  email), so the Google link works only for you. Changes save to the archived file as you type.
                </span>
              </div>
            </div>
            <div class="ar-guide-limit">
              <i class="bi bi-link-45deg" aria-hidden="true"></i>
              <div>
                <strong>Copy link</strong>
                <span>
                  Gives a CCMIS link to send to colleagues. It opens the file for anyone with access to the cabinet (after
                  they log in), and for nobody else.
                </span>
              </div>
            </div>
          </div>
          <ul class="ar-guide-list roomy">
            <li>
              Whether you can <strong>edit</strong> or only <strong>view</strong> in Google follows your CCMIS access:
              Viewers view; Contributors edit what they added; Managers edit everything in the cabinet.
            </li>
            <li>
              Your access in Google goes away by itself when you leave the cabinet, when your access changes, or when the
              file goes to the Trash.
            </li>
            <li>
              If your CCMIS email isn't a Google account (Gmail or ched.gov.ph), files can't open in Google for you:
              download them instead, or ask the administrator to update your email.
            </li>
            <li>PDFs and pictures open in the CCMIS preview. Files uploaded before the Archives moved to Google Drive stay on the CCMIS server and are downloaded as before.</li>
          </ul>
        </section>

        <!-- Access -->
        <section v-else-if="topic === 'access'">
          <h3>Who can see what</h3>
          <p class="ar-guide-lead">Access is given in two steps.</p>
          <ol class="ar-guide-steps">
            <li>
              <strong>Roles &amp; Permissions: the "Archives" permission</strong>
              <span>
                Lets people with that role open the Archives. On its own it shows no cabinets: they still need to be added
                to one.
              </span>
            </li>
            <li>
              <strong>Manage access, on each cabinet</strong>
              <span>
                The cabinet's managers (or an Archives administrator) add people as Viewer, Contributor or Manager. People
                see only the cabinets they're added to.
              </span>
            </li>
          </ol>
          <dl class="ar-guide-levels">
            <div v-for="level in levels" :key="level">
              <dt><span class="ar-badge" :class="level"><i class="bi" :class="ACCESS[level].icon"></i> {{ ACCESS[level].label }}</span></dt>
              <dd>{{ ACCESS[level].hint }}</dd>
            </div>
          </dl>
          <h4>Archives administrators</h4>
          <p>
            The Administrator role, and roles given <strong>Archives: manage cabinets and access</strong>, open and manage
            every cabinet, add cabinets, and change the upload settings. Give this only to the few people who run the Archives.
          </p>
          <p class="ar-guide-callout warn">
            <i class="bi bi-exclamation-triangle" aria-hidden="true"></i>
            <span>
              In Roles &amp; Permissions, ticking the whole <strong>Office Documents</strong> group also ticks the
              administrator permission. For regular staff, tick <strong>Archives</strong> on its own.
            </span>
          </p>
        </section>

        <!-- Trash and history -->
        <section v-else-if="topic === 'trash'">
          <h3>Trash and history</h3>
          <ul class="ar-guide-list roomy">
            <li>
              <strong>Delete</strong> moves things to the cabinet's <strong>Trash</strong>, so nothing is lost straight away.
              Open the Trash to restore them. A deleted folder comes back with everything that was in it.
            </li>
            <li>Contributors can delete and restore what they added. Managers can restore anything.</li>
            <li>
              Managers can also <strong>delete for good</strong>, one item at a time or with <em>Empty Trash</em>. Files in the
              Trash still use the cabinet's storage until then.
            </li>
            <li>
              Managers can open <strong>Activity</strong> to see who uploaded, opened, downloaded, moved or deleted what, and
              when, including who opened or closed uploads.
            </li>
          </ul>
        </section>

        <footer class="ar-guide-pager">
          <button v-if="prev" type="button" class="btn btn-light btn-sm" @click="go(prev.key)">
            <i class="bi bi-arrow-left mr-1"></i> {{ prev.label }}
          </button>
          <span></span>
          <button v-if="next" type="button" class="btn btn-light btn-sm" @click="go(next.key)">
            {{ next.label }} <i class="bi bi-arrow-right ml-1"></i>
          </button>
        </footer>
      </article>
    </div>
  </ArchiveModal>
</template>

<script>
import ArchiveModal from "./ArchiveModal.vue";
import { ACCESS, ACCESS_ORDER, maxSizeText } from "../archiveHelpers";

const TOPICS = [
  { key: "start", label: "Getting started", icon: "bi-signpost-2" },
  { key: "where", label: "What goes where", icon: "bi-diagram-3" },
  { key: "upload", label: "Uploading", icon: "bi-cloud-arrow-up" },
  { key: "names", label: "Folders and names", icon: "bi-folder2-open" },
  { key: "find", label: "Finding files", icon: "bi-search" },
  { key: "google", label: "Google Docs and links", icon: "bi-google" },
  { key: "access", label: "Who can see what", icon: "bi-shield-lock" },
  { key: "trash", label: "Trash and history", icon: "bi-trash3" },
];

const OFFICES = [
  {
    name: "Accounting",
    icon: "bi-calculator",
    color: "#0f9d58",
    examples: "Disbursement vouchers, payrolls, liquidation reports, official receipts, journal entry vouchers.",
  },
  {
    name: "Budget",
    icon: "bi-pie-chart",
    color: "#00a3a3",
    examples: "SAROs, NCAs, obligation and budget utilization requests (ORS/BURS), budget proposals, registries of allotments.",
  },
  {
    name: "Procurement",
    icon: "bi-cart3",
    color: "#f4801f",
    examples: "Purchase requests, requests for quotation, abstracts of quotations, purchase orders, BAC resolutions, contracts.",
  },
  {
    name: "Human Resources",
    icon: "bi-people",
    color: "#a142f4",
    examples: "201 files, appointments, service records, leave applications, training certificates, IPCR and OPCR.",
  },
  {
    name: "Records",
    icon: "bi-archive",
    color: "#4285f4",
    examples: "Incoming and outgoing communications, memorandums, special orders, office orders.",
  },
];

/** How to use the Archives: what goes where, uploading and limits, naming, finding, access and the Trash. */
export default {
  name: "ArchiveGuideModal",
  components: { ArchiveModal },
  props: {
    open: { type: Boolean, default: false },
    // Topic to open on: start, where, upload, names, find, access, trash
    initialTopic: { type: String, default: "start" },
    // { max_file_mb, groups: [{ key, label, extensions }] }
    limits: { type: Object, default: null },
    manageAll: { type: Boolean, default: false },
  },
  data() {
    return { ACCESS, levels: ACCESS_ORDER, topics: TOPICS, offices: OFFICES, topic: "start" };
  },
  computed: {
    maxSize() {
      return maxSizeText(this.limits);
    },
    groups() {
      return (this.limits && this.limits.groups) || [];
    },
    index() {
      return this.topics.findIndex((t) => t.key === this.topic);
    },
    prev() {
      return this.topics[this.index - 1] || null;
    },
    next() {
      return this.topics[this.index + 1] || null;
    },
  },
  watch: {
    open(value) {
      if (value) this.go(this.topics.some((t) => t.key === this.initialTopic) ? this.initialTopic : "start");
    },
  },
  methods: {
    go(key) {
      this.topic = key;
      this.$nextTick(() => {
        const body = this.$refs.body;
        if (body) body.scrollTop = 0;
        // On phones the topics scroll sideways; keep the chosen one in view.
        const on = this.$refs.nav && this.$refs.nav.querySelector(".on");
        if (on && on.scrollIntoView) on.scrollIntoView({ block: "nearest", inline: "nearest" });
      });
    },
  },
};
</script>
